import api from '../../../services/api'
import type { BruktbilFilters } from '../useBruktbilFilters'
import i18n from '../../../i18n'

/**
 * Markedsandel per forhandler for one merke, or one (merke, modell) pair
 * (#489 AC5/AC6).
 *
 * `actor_type` narrows the *seller* side — the Selgere page's convention. The
 * accepted values are the six the column actually carries; note that the one
 * meaning "forhandler" is BILBRANSJE, not FIRMA. The issue named
 * PERSON/FIRMA/UKJENT, which the data does not use.
 */
export const ACTOR_TYPES = [
  'BILBRANSJE',
  'PERSON',
  'LEASING',
  'FIRMA',
  'BILUTLEIE',
  'FORSIKRING',
] as const

export type ActorType = (typeof ACTOR_TYPES)[number]

/** Display label for an actor type, or undefined for a token we don't know. Call at render. */
export function actorTypeLabel(actorType: string): string | undefined {
  if (!(ACTOR_TYPES as readonly string[]).includes(actorType)) return undefined
  return i18n.t(`actorTypes.${actorType as ActorType}`, { ns: 'bruktbil' })
}

export interface ForhandlerAndelRow {
  org_nr: string | null
  org_name: string | null
  count: number
  andel: number // share of this merke/modell's eierskifter (0..1)
}

export interface ActorTypeShare {
  actor_type: string
  count: number
  share: number
}

export interface MerkeDetaljResponse {
  merke: string
  modell: string | null
  total: number
  forhandlere: ForhandlerAndelRow[]
  forhandlere_total: number
  actor_type_breakdown: ActorTypeShare[]
}

type BaseParams = Pick<
  BruktbilFilters,
  'from' | 'to' | 'klasse' | 'varebil' | 'omregistrering'
> & { selgerPostnummer?: string | null; kjoperPostnummer?: string | null }

export interface DetaljOptions {
  actorType?: ActorType | null
  limit?: number
  offset?: number
}

function buildQs(filters: BaseParams, extras: Record<string, string | number>): string {
  const qs = new URLSearchParams()
  qs.set('from_date', filters.from)
  qs.set('to_date', filters.to)
  for (const k of filters.klasse) qs.append('klasse', k)
  if (filters.varebil !== undefined) qs.set('varebil', String(filters.varebil))
  if (filters.omregistrering) qs.set('omregistrering', 'true')
  if (filters.selgerPostnummer)
    for (const p of filters.selgerPostnummer.split(',')) qs.append('selger_postnummer', p.trim())
  if (filters.kjoperPostnummer)
    for (const p of filters.kjoperPostnummer.split(',')) qs.append('kjoper_postnummer', p.trim())
  for (const [key, val] of Object.entries(extras)) qs.set(key, String(val))
  return qs.toString()
}

function detaljExtras(opts: DetaljOptions): Record<string, string | number> {
  const extras: Record<string, string | number> = {
    limit: opts.limit ?? 50,
    offset: opts.offset ?? 0,
  }
  if (opts.actorType) extras.actor_type = opts.actorType
  return extras
}

export function fetchMerkeDetalj(
  merke: string,
  filters: BaseParams,
  opts: DetaljOptions = {},
): Promise<{ data: MerkeDetaljResponse }> {
  const qs = buildQs(filters, { merke, ...detaljExtras(opts) })
  return api.get(`/bruktbil/merke-modell/merke/detalj?${qs}`)
}

export function fetchModellDetalj(
  merke: string,
  modell: string,
  filters: BaseParams,
  opts: DetaljOptions = {},
): Promise<{ data: MerkeDetaljResponse }> {
  const qs = buildQs(filters, { merke, modell, ...detaljExtras(opts) })
  return api.get(`/bruktbil/merke-modell/modell/detalj?${qs}`)
}
